import { t } from '@lingui/macro'
import { useCallback } from 'react'
import ReactPlayer from 'react-player'
import { useTheme } from 'styled-components'

import { Checkbox } from '@/@bothrs-ui/web/components/forms/checkbox/Checkbox'
import { DateInput } from '@/@bothrs-ui/web/components/forms/date-input/DateInput'
import { Input } from '@/@bothrs-ui/web/components/forms/input'
import { Label } from '@/@bothrs-ui/web/components/forms/label'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H2 } from '@/@bothrs-ui/web/components/typography/Heading'
import { SelectListItem } from '@/modules/questionnaire/components/select-list-item'
import { renderText } from '@/modules/shared/helpers/text-rendering'
import { StSignableDocumentWrapper } from './SignableDocumentQuestion.styled'
import { StPlayerWrapper } from './SignableDocumentWrapper.styled'

import type { SignableDocumentDetailField, SignableDocumentDetailFieldType } from '../../types/documents.types'

type SignableDocumentQuestionProps = {
  nested?: boolean
  noMargin?: boolean
  data: SignableDocumentDetailField
}

const TEXT_INPUT_TYPES: Set<SignableDocumentDetailFieldType> = new Set(['text', 'date'])

const SignableDocumentQuestion = ({ data, nested, noMargin }: SignableDocumentQuestionProps) => {
  const {
    label: title,
    description,
    id: questionId,
    options,
    placeholder,
    required,
    type: dataType,
    readonly,
    hidden,
    url,
  } = data
  const id = questionId.toString()

  const theme = useTheme()

  // Render Methods
  // ------------------------------------------------------------------------- /
  const renderTextInput = useCallback(() => {
    const type = dataType.toLowerCase()

    if (type === 'date') {
      return <DateInput type={'date'} name={id} readOnly={readonly} placeholder={placeholder} required={required} />
    }

    return (
      <Input
        name={id}
        type={type}
        inputMode={type === 'number' ? 'decimal' : 'text'}
        placeholder={placeholder}
        required={required}
        noMargin={noMargin}
        readOnly={readonly}
      />
    )
  }, [id, dataType, required, placeholder, noMargin, readonly])

  const renderDisplay = useCallback(() => {
    return renderText(title)
  }, [title])

  const renderCheckbox = useCallback(
    (isVideo: boolean) => {
      return (
        <Checkbox
          style={{
            width: '100%',
            border: `1px solid ${theme.Components.Conversational.Border}`,
            borderRadius: theme.UI.BorderRadius.Small,
            padding: theme.UI.SpacingPx.Space3,
          }}
          name={id}
          id={id}
          type={'checkbox'}
          label={
            isVideo
              ? t({
                  id: 'signable.form.checkbox.label.video',
                  message: 'Ik heb de video bekeken',
                })
              : options?.[0]?.label || ''
          }
          required={required}
          readOnly={readonly}
        />
      )
    },
    [id, required, options, theme, readonly],
  )

  const renderSingleSelect = useCallback(() => {
    return (
      <FlexColumn gap={8}>
        {options?.map((option, index) => {
          const { title, description, aside } = getLabelParts(option.label)
          const value = option.value || title.trim()
          return (
            <SelectListItem
              type='radio'
              key={`${id}_${index}`}
              id={`${id}_${index}`}
              name={id}
              value={value}
              label={title}
              description={option?.description || description}
              aside={aside}
              readOnly={readonly}
            />
          )
        })}
      </FlexColumn>
    )
  }, [options, id, readonly])

  const renderUrl = useCallback(() => {
    // if the url is a youtube or vimeo url, render the video player
    if (url?.includes('youtube') || url?.includes('vimeo')) {
      return (
        <StPlayerWrapper>
          <ReactPlayer
            url={url}
            controls={true}
            width='100%'
            height='100%'
            style={{ position: 'absolute', top: 0, left: 0 }}
          />
        </StPlayerWrapper>
      )
    }
  }, [url])

  return (
    <StSignableDocumentWrapper hidden={hidden} className='signable-document-question'>
      {dataType === 'display' ? null : (
        <>
          {title ? <H2>{title}</H2> : null}
          {description ? (
            nested ? (
              <Label htmlFor=''>{description}</Label>
            ) : (
              <P type='BodyLargeRegular'>{description}</P>
            )
          ) : null}
        </>
      )}
      {url ? renderUrl() : null}
      {TEXT_INPUT_TYPES.has(dataType)
        ? renderTextInput()
        : dataType === 'select'
          ? renderSingleSelect()
          : dataType === 'display'
            ? renderDisplay()
            : dataType === 'checkbox'
              ? renderCheckbox(!!url?.includes('youtube'))
              : null}
    </StSignableDocumentWrapper>
  )
}

export { SignableDocumentQuestion }

// This is a workaround because HB doesn't support description fields yet
// example text 1: Gezinskamer *rode tekst hier*|Dit zou op een volgende lijn moeten komen.
// example text 2:  *rode tekst hier* Gezinskamer |Dit zou op een volgende lijn moeten komen.
// using the example texts, the bold part is 'rode tekst hier' and the description is 'Dit zou op een volgende lijn moeten komen.'
// keep the rest of the first part as title
const getLabelParts = (label: string) => {
  // Split on newline or pipe
  const labelParts = label.split(/\n|\|/)

  // Bold part is the part between asterisks
  const aside = labelParts[0].split('*')?.[1]
  return {
    title: labelParts[0].replace(`*${aside}*`, ''),
    description: labelParts[1],
    aside,
  }
}
